import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api.js";
import { ErrorBox, VerdictPill, fmtDate, fmtRelative, CopyButton } from "../components/helpers.jsx";

function StatusPill({ status, endReason }) {
    const map = {
        active:  "allow",
        ended:   "lifted",
        expired: "deny",
        banned:  "ban",
        denied:  "deny",
    };
    const label = (status === "expired" && endReason === "heartbeat_timeout")
        ? "timed out"
        : status;
    return <span className={`pill ${map[status] ?? "lifted"}`}>{label}</span>;
}

export default function SessionDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let alive = true;
        api.get(`/api/admin/sessions/${id}`)
            .then((d) => { if (alive) setData(d); })
            .catch((e) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, [id, reload]);

    useEffect(() => {
        if (data?.session?.status !== "active") return;
        const t = setInterval(() => setReload(Date.now()), 5_000);
        return () => clearInterval(t);
    }, [data?.session?.status]);

    if (!data) {
        return (
            <>
                <div className="page-header"><h1>Session #{id}</h1></div>
                <ErrorBox>{err}</ErrorBox>
                {!err && <div className="text-dim">Loading…</div>}
            </>
        );
    }

    const s = data.session;

    return (
        <>
            <div className="page-header">
                <div>
                    <h1>Session #{id}</h1>
                    <div className="sub"><Link to="/sessions">← all sessions</Link></div>
                </div>
                <StatusPill status={s.status} endReason={s.endReason} />
            </div>

            <div className="card">
                <h2>Summary</h2>
                <table className="data">
                    <tbody>
                        <tr><th>Subject</th><td>
                            {s.subjectLabel && (
                                <div style={{ fontWeight: 500 }}>{s.subjectLabel}</div>
                            )}
                            <div className="mono text-dim">{s.subject}</div>
                        </td></tr>
                        <tr><th>HWID</th><td className="mono">{s.hwid ?? "—"}</td></tr>
                        <tr><th>IP</th><td className="mono">{s.ip ?? "—"}</td></tr>
                        <tr><th>Session token</th><td>
                            <div className="copy-row">
                                <div className="mono">{s.sessionToken}</div>
                                <CopyButton text={s.sessionToken} />
                            </div>
                        </td></tr>
                        <tr><th>Beats</th><td className="mono">{s.beats}</td></tr>
                        <tr><th>Last verdict</th><td>{s.lastVerdict ? <VerdictPill verdict={s.lastVerdict} /> : "—"}</td></tr>
                        <tr><th>Started</th><td>{fmtDate(s.createdAt)}</td></tr>
                        <tr><th>Last beat</th><td>{fmtDate(s.lastSeenAt)} ({fmtRelative(s.lastSeenAt)})</td></tr>
                        <tr><th>Expires</th><td>{fmtDate(s.expiresAt)}</td></tr>
                        {s.endedAt && (<>
                            <tr><th>Ended</th><td>{fmtDate(s.endedAt)}</td></tr>
                            <tr><th>End reason</th><td>{s.endReason ?? "—"}</td></tr>
                        </>)}
                    </tbody>
                </table>
            </div>

            <div className="table-wrap">
                <div className="table-toolbar">
                    <strong>Attempts under this session</strong>
                    <div className="spacer" />
                    <span className="text-dim">{data.attempts.length} shown (most recent 100)</span>
                </div>
                <table className="data">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Endpoint</th>
                            <th>Verdict</th>
                            <th>Category</th>
                            <th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.attempts.length === 0 && (
                            <tr><td colSpan={5} className="text-dim" style={{ padding: 24, textAlign: "center" }}>
                                No attempts yet (waiting for first heartbeat)
                            </td></tr>
                        )}
                        {data.attempts.map((r) => (
                            <tr key={r.id}>
                                <td title={new Date(r.createdAt).toLocaleString()}>
                                    <Link to={`/attempts/${r.id}`}>{fmtRelative(r.createdAt)}</Link>
                                </td>
                                <td>{r.endpoint}</td>
                                <td><VerdictPill verdict={r.verdict} /></td>
                                <td>{r.category ?? "—"}</td>
                                <td className="text-dim" style={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.reason ?? ""}>
                                    {r.reason ?? "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
